import { useTranslation } from '@/utils/hook'
import { libreBaskerville as editorial } from '@/fonts'
import Image from 'next/image'
import {
  RiArrowRightLine,
  RiGithubFill,
  RiArrowRightUpLine
} from '@remixicon/react'
import styles from './about.module.css'

const chapters = [
  { id: 'background', title: 'Background' },
  { id: 'manifesto', title: 'Manifesto' },
  { id: 'open-source', title: 'Open source' },
  { id: 'build-in-public', title: 'Build in public' }
]

const founders = [
  {
    name: 'Luo Baishun',
    photo: '/static/team-1.jpg',
    href: 'https://x.com/LuoSays'
  },
  {
    name: 'Wu Qin',
    photo: '/static/team-3.jpg',
    href: 'https://x.com/mufeng_me'
  }
]

export function Banner(): JSX.Element {
  const t = useTranslation()
  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <header className={styles.intro}>
          <h1 className={`${styles.title} ${editorial.className}`}>
            {t('aboutUs.bannerHeadline')}
          </h1>
          <p className={styles.subtitle}>{t('aboutUs.bannerText')}</p>
          <p className={styles.lead}>
            Form-building should be simple, affordable, and accessible to
            everyone. We’re building the tool we wanted for our own businesses.
          </p>
          <div className={styles.founders} aria-label="HeyForm co-founders">
            {founders.map(founder => (
              <a
                key={founder.name}
                href={founder.href}
                className={styles.founder}
              >
                <Image
                  src={founder.photo}
                  alt=""
                  width={48}
                  height={48}
                  className={styles.portrait}
                />
                <span>
                  <span className={styles.name}>
                    {founder.name}
                    <RiArrowRightUpLine aria-hidden="true" />
                  </span>
                  <span className={styles.role}>Co-founder</span>
                </span>
              </a>
            ))}
          </div>
        </header>

        <div className={styles.storyLayout}>
          <aside className={styles.sidebar}>
            <nav aria-label="Our story chapters" className={styles.chapters}>
              {chapters.map(chapter => (
                <a key={chapter.id} href={`#${chapter.id}`}>
                  {chapter.title}
                </a>
              ))}
            </nav>
          </aside>

          <article className={styles.story} aria-label="The HeyForm story">
            <section
              id="background"
              className={styles.chapter}
              aria-labelledby="background-title"
            >
              <h2 id="background-title">Background</h2>
              <p className={styles.opening}>
                The form builder space is crowded, but we felt something was
                missing.
              </p>
              <p>
                After running our own businesses (and yes, they all failed), we
                constantly found ourselves frustrated with the options out
                there.
              </p>
              <p>
                Existing form builders either looked outdated, were overpriced
                for small businesses, or simply hadn’t been updated in years.
                Others lacked the features we desperately needed.
              </p>
              <p>
                So, we decided to explore the idea of creating something better:
                a form builder that’s not just functional and feature-rich, but
                affordable for everyone.
              </p>
            </section>

            <section
              id="manifesto"
              className={styles.chapter}
              aria-labelledby="manifesto-title"
            >
              <h2 id="manifesto-title">Manifesto</h2>
              <p>
                At HeyForm, we believe in making form-building simple,
                affordable, and accessible to everyone.
              </p>
              <p>
                Our mission is to empower businesses of all sizes with the tools
                they need to gather and manage data effortlessly. We value
                transparency in everything we do, from pricing to product
                updates.
              </p>
              <p>
                Innovation drives us, but it’s our community that inspires us to
                keep improving. We’re committed to building a platform that
                works for you, because we know that when you succeed, so do we.
              </p>
            </section>

            <section
              id="open-source"
              className={styles.chapter}
              aria-labelledby="open-source-title"
            >
              <h2 id="open-source-title">Open source</h2>
              <p>
                HeyForm wasn’t always open-source, but as a small team of just
                two, we knew that collaboration was key. We decided to open
                HeyForm up to the world, inviting talented developers to
                contribute and make it even better.
              </p>
              <p>
                The response was incredible. HeyForm quickly became a favorite
                in the open-source community. We hit #1 on the trending list for
                days and gained over 6,000 stars in just the first four months.
              </p>
              <div className={styles.statement}>
                <p className={editorial.className}>
                  We build the software.
                  <br />
                  You own it.
                </p>
              </div>
              <p>
                You can fork the project, contribute to its development, or join
                a community that helps you make it your own. Together, we’re
                creating a tool that’s not only powerful but also shaped by the
                people who use it.
              </p>
            </section>

            <section
              id="build-in-public"
              className={styles.chapter}
              aria-labelledby="build-in-public-title"
            >
              <h2 id="build-in-public-title">Build in public</h2>
              <p>
                At HeyForm, we believe in the power of transparency. That’s why
                we build in public. We share our development process and
                progress openly, inviting our community to join us every step of
                the way.
              </p>
              <p>
                Your feedback and suggestions shape our updates and
                improvements, making HeyForm better with each iteration.
              </p>
              <p>
                Building in public means you get to see the journey, from the
                initial idea to the final product. It’s an ongoing collaboration
                that keeps us accountable and ensures that we create a tool that
                truly meets your needs.
              </p>
              <a
                className={styles.textLink}
                href="https://github.com/heyform/heyform/releases"
              >
                Follow product updates
                <RiArrowRightLine aria-hidden="true" />
              </a>
            </section>

            <section
              className={styles.invitation}
              aria-labelledby="about-invitation-title"
            >
              <h2 id="about-invitation-title">Help shape what comes next.</h2>
              <p>
                Explore the code, contribute an idea, or tell us what you need.
                We’re building HeyForm together.
              </p>
              <div className={styles.actions}>
                <a
                  className={styles.button}
                  href="https://github.com/heyform/heyform"
                >
                  <RiGithubFill aria-hidden="true" />
                  Explore on GitHub
                </a>
                <a className={styles.textLink} href="/contact">
                  Talk to us
                  <RiArrowRightLine aria-hidden="true" />
                </a>
              </div>
            </section>
          </article>
        </div>
      </div>
    </main>
  )
}
